import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LearningReminderModal } from "../LearningReminderModal";
import { DocsChatbot } from "../DocsChatbot";
import { TrialExpiryModal } from "../TrialExpiryModal";
import { ConnectBankReminder } from "../ConnectBankReminder";
import { Home, ArrowLeftRight, PieChart, Receipt, Settings, LogOut, RefreshCw, Target, MoreHorizontal, Wallet, TrendingUp, PiggyBank, Clock } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { toast } from "sonner";

var mainNav = [
  { path: "/", icon: Home, label: "Home" },
  { path: "/transactions", icon: ArrowLeftRight, label: "Activity" },
  { path: "/budget", icon: PieChart, label: "Budget" },
  { path: "/bills", icon: Receipt, label: "Bills" },
];
var moreNav = [
  { path: "/net-worth", icon: PiggyBank, label: "Net Worth" },
  { path: "/goals", icon: Target, label: "Goals" },
  { path: "/cash-flow", icon: TrendingUp, label: "Cash Flow" },
  { path: "/debt-payoff", icon: Wallet, label: "Debt Payoff" },
];

var CSS = `
.tl-app{min-height:100vh;font-family:'Hanken Grotesk',-apple-system,sans-serif;background:#FBF9F3;color:#23241E;display:flex}
.tl-side{position:fixed;inset:0 auto 0 0;width:240px;background:#fff;border-right:1px solid #E8E2D5;display:none;flex-direction:column;z-index:50}
@media(min-width:1024px){.tl-side{display:flex}}
.tl-side-logo{display:flex;align-items:center;gap:10px;padding:20px 22px;height:64px}
.tl-side-logo img{height:26px}
.tl-side-nav{flex:1;padding:8px 10px;overflow-y:auto}
.tl-side-nav .group-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:#a6a698;padding:16px 14px 6px}
.tl-nav-item{display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:12px;font-size:14.5px;font-weight:500;color:#6f6f64;text-decoration:none;transition:all .15s;margin-bottom:2px}
.tl-nav-item:hover{background:rgba(20,67,43,.04);color:#23241E}
.tl-nav-item.active{background:rgba(126,217,87,.1);color:#14432B;font-weight:600}
.tl-nav-item svg{width:20px;height:20px;flex-shrink:0}
.tl-side-user{padding:12px;border-top:1px solid #E8E2D5}
.tl-avatar{width:36px;height:36px;border-radius:50%;background:rgba(126,217,87,.12);display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#14432B;flex-shrink:0}
.tl-main{flex:1;min-width:0}
@media(min-width:1024px){.tl-main{margin-left:240px}}
.tl-topbar{position:sticky;top:0;z-index:40;height:56px;display:flex;align-items:center;justify-content:space-between;padding:0 20px;background:rgba(251,249,243,.92);backdrop-filter:blur(12px);border-bottom:1px solid #E8E2D5}
.tl-topbar-logo{display:flex;align-items:center;gap:8px}
.tl-topbar-logo img{height:22px}
@media(min-width:1024px){.tl-topbar-logo{display:none}}
.tl-sync{display:flex;align-items:center;gap:10px}
.tl-sync .dot{width:7px;height:7px;border-radius:50%;background:#7ED957;display:inline-block;margin-right:4px}
.tl-sync .time{font-size:12.5px;color:#a6a698}
.tl-sync-btn{display:inline-flex;align-items:center;gap:6px;padding:7px 16px;border-radius:50px;border:1.5px solid #E8E2D5;background:#fff;font-size:13.5px;font-weight:600;color:#14432B;cursor:pointer;transition:all .15s;font-family:inherit}
.tl-sync-btn:hover{border-color:#14432B;background:#f0f7f2}
.tl-sync-btn:disabled{opacity:.5;cursor:wait}
.tl-sync-btn svg{width:16px;height:16px}
.tl-content{padding:24px 20px 100px}
@media(min-width:768px){.tl-content{padding:28px 32px 40px}}
@media(min-width:1024px){.tl-content{padding:32px 40px 40px}}
.tl-mobile-nav{position:fixed;bottom:0;left:0;right:0;z-index:50;background:rgba(255,255,255,.92);backdrop-filter:blur(12px);border-top:1px solid #E8E2D5;padding-bottom:env(safe-area-inset-bottom)}
@media(min-width:1024px){.tl-mobile-nav{display:none}}
.tl-mobile-nav-in{display:flex;align-items:center;justify-content:space-around;height:58px}
.tl-mob-item{display:flex;flex-direction:column;align-items:center;gap:3px;font-size:10px;font-weight:500;color:#a6a698;text-decoration:none;padding:4px 8px;transition:color .15s}
.tl-mob-item.active{color:#14432B}
.tl-mob-item svg{width:22px;height:22px}
@keyframes tl-spin{to{transform:rotate(360deg)}}
.tl-spinning{animation:tl-spin 1s linear infinite}
`;

export const AppLayout = () => {
  var { user, logout, api } = useAuth();
  var navigate = useNavigate();
  var [lastUpdated, setLastUpdated] = useState(null);
  var [syncing, setSyncing] = useState(false);
  var [plaidConnected, setPlaidConnected] = useState(null);
  var [onboardingCompleted, setOnboardingCompleted] = useState(null);
  var [onboardingSkipped, setOnboardingSkipped] = useState(false);
  var [moreSheetOpen, setMoreSheetOpen] = useState(false);
  var [syncLimitDialog, setSyncLimitDialog] = useState({ open: false, message: "" });

  var checkPlaidStatus = useCallback(async () => {
    try {
      var response = await api.get("/plaid/status");
      setPlaidConnected(response.data.connected);
      setOnboardingCompleted(response.data.onboarding_completed);
      setOnboardingSkipped(response.data.onboarding_skipped || false);
      if (response.data.connected) setLastUpdated(new Date());
    } catch (error) {
      setPlaidConnected(false);
      setOnboardingCompleted(false);
    }
  }, [api]);

  useEffect(() => { checkPlaidStatus(); }, [checkPlaidStatus]);
  useEffect(() => { if (onboardingCompleted === false) navigate("/onboarding"); }, [onboardingCompleted, navigate]);

  var handleSync = async () => {
    setSyncing(true);
    try {
      var response = await api.post("/plaid/sync");
      setLastUpdated(new Date());
      toast.success(response.data.message || "Sync completed!");
    } catch (error) {
      if (error.response?.status === 429) {
        setSyncLimitDialog({ open: true, message: error.response?.data?.detail || "You can only sync once per day." });
      } else {
        toast.error(error.response?.data?.detail || "Failed to sync.");
      }
    } finally { setSyncing(false); }
  };

  var handleLogout = () => { logout(); navigate("/login"); };

  var formatTime = () => {
    if (!lastUpdated) return "Never";
    var diff = Math.floor((new Date() - lastUpdated) / 60000);
    if (diff < 1) return "Just now";
    if (diff < 60) return diff + "m ago";
    return lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  var initials = user?.name ? user.name.split(" ").map(function(n){return n[0];}).join("").toUpperCase().slice(0,2) : "U";

  if (onboardingCompleted === null) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#FBF9F3"}}>
      <img src="/tally-logo.png" alt="Tally" style={{height:32,opacity:.5}} />
    </div>
  );
  if (onboardingCompleted === false) return null;

  return (
    <div className="tl-app">
      <style>{CSS}</style>

      {/* Desktop Sidebar */}
      <aside className="tl-side">
        <div className="tl-side-logo"><img src="/tally-logo.png" alt="Tally" /></div>
        <nav className="tl-side-nav">
          <div className="group-label">Main</div>
          {mainNav.map(function(item) {
            return <NavLink key={item.path} to={item.path} end={item.path==="/"} className={function(p){return "tl-nav-item"+(p.isActive?" active":"");}} data-testid={"nav-"+item.label.toLowerCase()}>
              <item.icon /><span>{item.label}</span>
            </NavLink>;
          })}
          <div className="group-label" style={{marginTop:8}}>More</div>
          {moreNav.map(function(item) {
            return <NavLink key={item.path} to={item.path} className={function(p){return "tl-nav-item"+(p.isActive?" active":"");}} data-testid={"nav-"+item.label.toLowerCase().replace(" ","-")}>
              <item.icon /><span>{item.label}</span>
            </NavLink>;
          })}
        </nav>
        <div className="tl-side-user">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"10px 12px",border:"none",background:"transparent",borderRadius:12,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}} data-testid="user-menu-trigger">
                <div className="tl-avatar">{initials}</div>
                <div style={{flex:1,minWidth:0}}><div style={{fontSize:14,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{user?.name}</div><div style={{fontSize:12,color:"#a6a698",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{user?.email}</div></div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={function(){navigate("/settings");}} data-testid="menu-settings"><Settings className="mr-2 h-4 w-4" />Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Area */}
      <div className="tl-main">
        <ConnectBankReminder plaidConnected={plaidConnected} onboardingSkipped={onboardingSkipped} />

        {/* Top Bar */}
        <div className="tl-topbar">
          <div className="tl-topbar-logo"><img src="/tally-logo.png" alt="Tally" /></div>
          <div style={{flex:1}} />
          <div className="tl-sync">
            <span className="time" style={{display:"none"}}><span className="dot" />{formatTime()}</span>
            <button className="tl-sync-btn" onClick={handleSync} disabled={syncing} data-testid="sync-button">
              <RefreshCw className={syncing?"tl-spinning":""} /> Sync
            </button>
            {/* Mobile user */}
            <div style={{display:"block"}} className="lg:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button style={{width:36,height:36,borderRadius:"50%",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(126,217,87,.12)",color:"#14432B",fontWeight:700,fontSize:13,fontFamily:"inherit"}} data-testid="mobile-user-menu">{initials}</button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div style={{padding:"8px 12px"}}><div style={{fontWeight:600,fontSize:14}}>{user?.name}</div><div style={{fontSize:12,color:"#a6a698"}}>{user?.email}</div></div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={function(){navigate("/settings");}}><Settings className="mr-2 h-4 w-4" />Settings</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSync} disabled={syncing}><RefreshCw className={"mr-2 h-4 w-4"+(syncing?" tl-spinning":"")} />Sync now</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive"><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="tl-content"><Outlet /></div>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="tl-mobile-nav">
        <div className="tl-mobile-nav-in">
          {mainNav.map(function(item) {
            return <NavLink key={item.path} to={item.path} end={item.path==="/"} className={function(p){return "tl-mob-item"+(p.isActive?" active":"");}} data-testid={"mobile-nav-"+item.label.toLowerCase()}>
              <item.icon /><span>{item.label}</span>
            </NavLink>;
          })}
          <Sheet open={moreSheetOpen} onOpenChange={setMoreSheetOpen}>
            <SheetTrigger asChild>
              <button className="tl-mob-item" data-testid="mobile-nav-more"><MoreHorizontal style={{width:22,height:22}} /><span>More</span></button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-auto max-h-[70vh] rounded-t-3xl" style={{background:"#fff",borderColor:"#E8E2D5"}}>
              <SheetHeader><SheetTitle>More</SheetTitle></SheetHeader>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,padding:"16px 0 24px"}}>
                {moreNav.map(function(item) {
                  return <NavLink key={item.path} to={item.path} onClick={function(){setMoreSheetOpen(false);}} className={function(p){return "tl-mob-item"+(p.isActive?" active":"");}} style={{padding:20,borderRadius:14,background:"rgba(126,217,87,.04)",flexDirection:"column",gap:8}} data-testid={"sheet-nav-"+item.label.toLowerCase().replace(" ","-")}>
                    <item.icon /><span style={{fontSize:12}}>{item.label}</span>
                  </NavLink>;
                })}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <LearningReminderModal />
      <TrialExpiryModal />
      <DocsChatbot />

      <AlertDialog open={syncLimitDialog.open} onOpenChange={function(open){setSyncLimitDialog({...syncLimitDialog,open:open});}}>
        <AlertDialogContent style={{background:"#fff",borderColor:"#E8E2D5",borderRadius:20}}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{display:"flex",alignItems:"center",gap:8,fontFamily:"'Lora',Georgia,serif"}}><Clock className="h-5 w-5" style={{color:"#C68A3C"}} />Sync Limit Reached</AlertDialogTitle>
            <AlertDialogDescription>{syncLimitDialog.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogAction style={{background:"#14432B",color:"#F4EFE4",borderRadius:50}}>Got it</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
